import { Link } from "@tanstack/react-router";
import { LegalNav } from "../components/cookie-consent.tsx";
import { DreamLogo } from "../components/dream-logo.tsx";
import { ThemeToggle } from "../components/theme-toggle.tsx";
import { calendarSearch } from "../router.tsx";

/**
 * Política de Privacidade e Cookies — LGPD.
 *
 * Página pública: login, portal e painel apontam para cá. O texto descreve o
 * que o Dream Calendar realmente trata (conta, operação, sessão), sem inventar
 * rastreamento que o produto não faz.
 */

const UPDATED_AT = "6 de setembro de 2026";

export function PrivacyPage() {
  return (
    <div className="h-full overflow-y-auto bg-[var(--color-surface-subtle)]">
      <header className="border-b border-[var(--color-line)] bg-[var(--color-surface)] pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 sm:px-6">
          <DreamLogo />
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle compact />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-muted)]">
          Dream Vision · Dream Calendar
        </p>
        <h1 className="mt-1 text-[1.75rem] font-bold leading-tight tracking-[0] text-[var(--color-ink)]">
          Política de Privacidade e Cookies
        </h1>
        <p className="mt-2 text-[0.8125rem] text-[var(--color-ink-muted)]">
          Última atualização: {UPDATED_AT}. Esta política explica, em linguagem clara, como a Dream
          Vision trata dados pessoais no Dream Calendar, em cumprimento à Lei nº 13.709/2018 (LGPD).
        </p>

        <div className="mt-8 space-y-8 text-[0.9375rem] leading-6 text-[var(--color-ink-secondary)]">
          <Section id="controlador" title="1. Quem é o controlador">
            <p>
              O controlador dos dados é a <strong>Dream Vision</strong>, responsável pelo Dream
              Calendar em{" "}
              <span className="font-mono text-[0.8125rem]">calendar.dreamvision.com.br</span>.
              Pedidos sobre privacidade:{" "}
              <a
                className="font-semibold text-[#0074FF] hover:underline"
                href="mailto:equipe@dreamvision.com.br"
              >
                equipe@dreamvision.com.br
              </a>
              .
            </p>
          </Section>

          <Section id="dados" title="2. Quais dados tratamos">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Conta da equipe:</strong> e-mail e nome da conta Google @dreamvision.com.br.
                Não usamos senha própria: a autenticação fica com o Google.
              </li>
              <li>
                <strong>Operação do calendário:</strong> clientes, projetos, entregas, responsáveis,
                prazos, status e o que a equipe registra para produzir e publicar.
              </li>
              <li>
                <strong>Portal do cliente:</strong> acesso por link com token; se a equipe pediu
                senha, ela também é verificada. O cliente vê o calendário em modo leitura.
              </li>
              <li>
                <strong>Técnicos:</strong> cookie de sessão autenticada, preferência de tema e a sua
                escolha de cookies neste dispositivo. Logs de servidor com redaction de senha, token
                e cookie.
              </li>
            </ul>
          </Section>

          <Section id="finalidade" title="3. Para que usamos">
            <p>
              Operar o calendário de entregas da Dream Vision: autenticar a equipe, mostrar prazos
              ao cliente, registrar alterações e manter a sessão segura. Não vendemos dados, não
              fazemos perfilamento publicitário e não enviamos a base para redes de anúncio.
            </p>
          </Section>

          <Section id="base-legal" title="4. Base legal (LGPD)">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Execução de contrato / procedimento preliminar</strong> — conta da equipe e
                operação do calendário.
              </li>
              <li>
                <strong>Legítimo interesse</strong> — segurança da sessão, prevenção a abuso e logs
                técnicos.
              </li>
              <li>
                <strong>Consentimento</strong> — cookies não essenciais, quando existirem. Hoje o
                produto não usa cookies de marketing; o banner registra a sua escolha.
              </li>
            </ul>
          </Section>

          <Section id="cookies" title="5. Cookies">
            <p>
              Cookies são pequenos arquivos no navegador. No Dream Calendar eles se dividem assim:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <strong>Necessários:</strong> cookie de sessão <em>HttpOnly</em> para a equipe
                permanecer autenticada; preferência de tema; registro deste consentimento. Sem eles
                o login e o calendário não funcionam.
              </li>
              <li>
                <strong>Opcionais:</strong> não há cookies de publicidade, analytics de terceiros
                nem pixels sociais neste produto. Se isso mudar, esta política e o banner serão
                atualizados.
              </li>
            </ul>
            <p className="mt-2">
              Você pode aceitar os cookies ou ficar só com os necessários. A escolha fica neste
              dispositivo e pode ser alterada pelo link “Cookies” no rodapé.
            </p>
          </Section>

          <Section id="compartilhamento" title="6. Compartilhamento">
            <p>
              Dados operacionais ficam na infraestrutura da Dream Vision. O cliente vê apenas o que
              a equipe deixou visível no portal. Prestadores de hospedagem e e-mail processam dados
              como operadores, sob instrução nossa. Não há transferência internacional com
              finalidade de marketing.
            </p>
          </Section>

          <Section id="retencao" title="7. Por quanto tempo">
            <p>
              Contas e entregas permanecem enquanto a operação da Dream Vision usar o calendário, ou
              até exclusão a pedido. Sessões expiram. Links do portal podem ser revogados. Logs
              técnicos são retidos o mínimo necessário para segurança.
            </p>
          </Section>

          <Section id="direitos" title="8. Seus direitos">
            <p>
              Pelo art. 18 da LGPD, você pode confirmar a existência de tratamento, acessar,
              corrigir, anonimizar, bloquear ou eliminar dados desnecessários, pedir portabilidade,
              informar-se sobre compartilhamentos e revogar consentimento. Escreva para{" "}
              <a
                className="font-semibold text-[#0074FF] hover:underline"
                href="mailto:equipe@dreamvision.com.br"
              >
                equipe@dreamvision.com.br
              </a>
              . Também é possível reclamar à ANPD.
            </p>
          </Section>

          <Section id="seguranca" title="9. Segurança">
            <p>
              Sessão em cookie seguro, login com Google restrito a @dreamvision.com.br, autorização
              na API (não só na interface) e logs sem segredos. Nenhum sistema é infalível; se
              houver incidente relevante, a Dream Vision comunicará os titulares e a ANPD quando a
              lei exigir.
            </p>
          </Section>

          <Section id="atualizacoes" title="10. Alterações">
            <p>
              Quando esta política mudar de forma relevante, a data no topo é atualizada e, se
              necessário, o banner de cookies volta a aparecer.
            </p>
          </Section>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-[var(--color-line)] pt-6">
          <Link
            to="/login"
            className="text-[0.875rem] font-semibold text-[#0074FF] hover:underline"
          >
            Ir para o login
          </Link>
          <Link
            to="/calendario"
            search={calendarSearch()}
            className="text-[0.875rem] font-medium text-[var(--color-ink-muted)] hover:underline"
          >
            Abrir o calendário
          </Link>
          <LegalNav />
        </div>
      </main>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-[1.0625rem] font-bold tracking-[0] text-[var(--color-ink)]">{title}</h2>
      <div className="mt-2 space-y-2">{children}</div>
    </section>
  );
}
